import type { UndoOperation } from '@/composables/useUndoStack';

export interface CandidateHistoryRow {
  id: string;
  before: Record<string, unknown>;
  after: Record<string, unknown>;
}

interface BatchHistoryActions {
  compensate: (
    rows: Array<{ id: string; expected: Record<string, unknown>; values: Record<string, unknown> }>,
  ) => Promise<Map<string, Record<string, unknown>>>;
  deleteCreated: (ids: string[]) => Promise<void>;
  restoreCreated: (ids: string[]) => Promise<void>;
}

/** Снимки принадлежат транзакции, а не перечитанному после неё окну таблицы. */
export function createBatchHistory(
  label: string,
  historyRows: CandidateHistoryRow[],
  createdIds: string[],
  actions: BatchHistoryActions,
): UndoOperation | null {
  const created = [...createdIds];
  const createdSet = new Set(created);
  const changed = historyRows.filter((row) => !createdSet.has(row.id));
  if (!changed.length && !created.length) return null;
  let before = new Map(changed.map((row) => [row.id, structuredClone(row.before)]));
  let after = new Map(changed.map((row) => [row.id, structuredClone(row.after)]));
  // Если второй этап упал, повтор продолжает его без повторной правки ячеек.
  let fieldSide: 'before' | 'after' = 'after';
  const compensate = async (reverse: boolean) => {
    if (!changed.length) return;
    const actual = await actions.compensate(
      changed.map((row) => ({
        id: row.id,
        expected: (reverse ? after : before).get(row.id)!,
        values: (reverse ? before : after).get(row.id)!,
      })),
    );
    if (reverse) before = actual;
    else after = actual;
  };
  return {
    label,
    undo: async () => {
      if (fieldSide === 'after') {
        await compensate(true);
        fieldSide = 'before';
      }
      if (created.length) await actions.deleteCreated(created);
    },
    redo: async () => {
      // Сначала gate изменённых ячеек: конфликт не должен восстанавливать
      // созданные строки только частично выполненного повтора.
      if (fieldSide === 'before') {
        await compensate(false);
        fieldSide = 'after';
      }
      if (created.length) await actions.restoreCreated(created);
    },
  };
}
